'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale } from 'next-intl';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import Skeleton from '../../components/Skeleton';
import TileData from '../../components/TileData';
import { AccountLabels } from '../lib/labels';

import { pushGAEvent } from '@components/Analytics/Google/GAScript';
import { Button, Input } from '@components/Form';
import { toastError, toastSuccess } from '@components/root/NotificationProxy';
import { createPasswordSchema } from '@utils/api/validation/common';
import { useUserStore } from '@utils/user';
import { User } from 'types/user';

const createSchema = (errorMsg: string) =>
  z.object({
    email: z.string().email(),
    oldPassword: z.string().max(50),
    password: createPasswordSchema(errorMsg),
  });

interface Props {
  labels: AccountLabels;
  user: User | null;
}

const Account = ({ labels, user }: Props) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const userStore = useUserStore();
  const locale = useLocale();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createSchema(labels.passwordError)),
    defaultValues: {
      email: user?.email || '',
      oldPassword: '',
      password: '',
    },
  });

  useEffect(() => {
    if (!user) {
      return;
    }
    setValue('email', user?.email);
  }, [user]);

  const formSubmit = async (formData: any) => {
    setLoading(true);
    try {
      const result = await fetch(
        `${process.env.NEXT_PUBLIC_API_PUBLIC}/users/profile/edit/password`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.accessToken}`,
          },
          body: JSON.stringify(formData),
        },
      );
      if (result.ok) {
        userStore.fetchUser();
        toastSuccess(labels.toast.success);

        pushGAEvent(userStore, 'transinfo_password_change', locale, {
          transinfo_view_category: 'password-change-from-profile'
        });

      } else {
        toastError(labels.toast.error);
      }
    } catch (e) {
      toastError(labels.toast.error);
    }
    setLoading(false);
  };

  if (!userStore.isReady || !user) {
    return <Skeleton title={labels.title} numinputs={3} />;
  }

  return (
    <TileData title={labels.title}>
      <form className="flex flex-col gap-3" onSubmit={handleSubmit(formSubmit)}>
        <label htmlFor="email">
          {labels.email}
          <Input {...register('email')} disabled />
        </label>
        {userStore.provider === 'wordpress' && (
          <>
            <label htmlFor="oldPassword">
              {labels.oldPassword}
              <Input
                type="password"
                placeholder={labels.oldPassword}
                error={!!errors.oldPassword}
                disabled={isLoading}
                {...register('oldPassword')}
              />
            </label>
            {errors.oldPassword && (
              <p className="text-xs text-red-500">
                {errors.oldPassword.message}
              </p>
            )}
            <label htmlFor="password">
              {labels.password}
              <Input
                type="password"
                placeholder={labels.password}
                error={!!errors.password}
                disabled={isLoading}
                {...register('password')}
              />
              {errors?.password && (
                <p className="text-xs text-red-500">
                  {errors?.password.message}
                </p>
              )}
            </label>
            <Button loading={isLoading} disabled={isLoading}>
              {labels.save}
            </Button>
          </>
        )}
      </form>
    </TileData>
  );
};

export default Account;
